import type {
  ActiveShift,
  HeldRegistration,
  SequenceRecoveryRecord,
  TerminalIdentity,
} from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { IconDeviceDesktopOff } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

import {
  desktopAppVersion,
  desktopCatalogue,
  desktopPrinter,
  desktopPrinterList,
  desktopPrinterSetup,
  desktopRecentSales,
  desktopRegistrations,
  desktopSales,
  desktopSalesAcross,
  desktopSequences,
  desktopShift,
  desktopShiftTakings,
  desktopStock,
  desktopSync,
  desktopTerminal,
  desktopTickets,
  desktopUpdates,
} from '@/desktop/bridge';
import { systemClock } from '@/lib/clock';

import { offeredForSale } from './basket-outlets';
import { OutletSwitch, OutletSwitchDialog } from './OutletSwitch';
import { PrinterSetup } from './PrinterSetup';
import { SellAdmission } from './SellAdmission';
import { SellGoods } from './SellGoods';
import { counterName, sequenceRecoveryNotice } from './sequence-recovery-notice';
import { StartShift } from './StartShift';
import { TerminalOutOfService } from './TerminalOutOfService';
import { messageForSwitchRefusal, NOT_A_TILL } from './till-messages';
import { TillFrame, tillPlate } from './TillFrame';
import { UpdateReceived } from './UpdateReceived';

/**
 * The till, and the three questions it answers before a ticket can be sold.
 *
 * **Is this a terminal at all?** The same build serves a browser, where there is
 * no bridge, no lease and no printer. It says so plainly rather than rendering a
 * sale it could never complete.
 *
 * **May the device trade?** A lease is what makes a request from here a request
 * *at an outlet*, and without a **live** one nothing sold could ever be
 * recorded. Registered is not the same question — a withdrawn terminal is still
 * registered, and asking the wrong one of the two is issue #215 — so the gate is
 * the lease's status and all three standings that are not `live` land on
 * `TerminalOutOfService`. Putting the lease back is an owner's job in the back
 * office, so that screen says so *and* carries the way there: the remedy is one
 * a screen can offer even where it is not one this screen can perform.
 *
 * **Is anybody standing at it?** Every ticket is recorded against a person —
 * `audit_entries.actor_id` is NOT NULL on the server and Principle VII requires
 * every action be attributable — so the shift comes before the sale.
 *
 * **And which of the two counters is it?** A gate prints admission; a shop sells
 * goods over a counter. Both are this screen, and which one a terminal opens on
 * is read from what its own catalogue holds rather than configured — the outlet
 * comes from the lease, and what that outlet sells is the answer. A till holding
 * only one kind never sees a switch, because there is nothing to switch to.
 *
 * ## Why this route is declared public
 *
 * It is the one screen in the app whose gate is not the back-office session, and
 * `route-table.tsx` says so at length. The short of it: a cashier has a PIN and a
 * device, not a session, so `requireSignedIn()` would redirect the till to a
 * sign-in screen it can never pass. Declaring it public is honest about what the
 * *router* decides, which here is nothing. What actually gates it is the lease
 * and the shift, both held in the main process, and neither reachable from a
 * browser — which is why this screen opened in one can do exactly nothing.
 */
/** The two things a till does. Which one it opens on is read, never configured. */
type Counter = 'admission' | 'goods';

/** What this outlet's held catalogue actually offers, by counter. */
interface Offered {
  readonly admission: boolean;
  readonly goods: boolean;
}

export function Till() {
  // Beside every screen rather than inside one (#310), so it stays mounted —
  // and "already dismissed" stays remembered — as the till moves between
  // sign-on, the counters and out-of-service.
  return (
    <>
      <TillScreens />
      <UpdateReceived updates={desktopUpdates()} />
    </>
  );
}

function TillScreens() {
  const catalogue = desktopCatalogue();
  const tickets = desktopTickets();
  const sales = desktopSales();
  // Asked for again for the other outlets this machine sells for (#284): one
  // basket across them. Without it, each outlet sells on its own as before.
  const salesAcross = desktopSalesAcross();
  // And again for the two things the device's own sale log answers: what this
  // shift has taken, and what this till sold today. Separate handles for
  // separate screens, so a shell missing one keeps the other — and a shell
  // missing both sells exactly as it did before either existed.
  const takings = desktopShiftTakings();
  const recent = desktopRecentSales();
  const shiftBridge = desktopShift();
  const terminal = desktopTerminal();
  // The outlets this machine sells for (#243). Asked for apart from `terminal`
  // so a shell holding one registration, with neither call, still opens its
  // till — it simply names no outlet and offers no switch.
  const registrationsBridge = desktopRegistrations();
  // Status only, and it gates nothing: a shell that cannot answer for its
  // printer still sells, and the counter loses a warning rather than a till.
  const printer = desktopPrinter();
  // Setting it is a separate capability (#261): a shell that cannot is a
  // sign-on screen without the control, and nothing else changes.
  const printerSetup = desktopPrinterSetup();
  // Listing this PC's printers is one more (#282): without it, the typed field.
  const listsPrinters = desktopPrinterList() !== null;
  // The outlet's time-slot rule (#277), set beside the printer on the same terms.
  // And the shelves, on exactly the same terms. CL-801 is explicit that the till
  // warns and never refuses, so a shell that cannot answer for its stock is a
  // counter with one fewer warning on it and not a counter that stops.
  const stock = desktopStock();
  // Read-only, and it gates nothing. A shell that cannot answer this is a till
  // that does not explain its numbering, not a till that stops.
  const sequences = desktopSequences();

  // The build the running shell reports (#306), read once: it cannot change
  // without the window closing. A shell that cannot answer, or fails to, is
  // `null` and draws nothing — never "unknown".
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    desktopAppVersion()?.()
      .then((answer) => {
        if (live && typeof answer === 'string' && answer !== '') setVersion(answer);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, []);

  /**
   * What this device says it is, and — since issue #215 — what it says it may
   * do, which are two different facts.
   *
   * **Held as the whole identity rather than as `RegisteredTerminal | null`.**
   * It used to be narrowed on arrival with `answer.registered ? answer : null`,
   * and that discarded the only field that separates a till that can sell from
   * one whose lease has been withdrawn: both answered `registered: true`, so a
   * revoked terminal rendered the ordinary sign-on screen and refused every PIN
   * entered into it. The status is now carried and the gate below is on it.
   *
   * Read on mount, not polled. Revocation happens in the back office on another
   * machine and reaches this window on its next read — which is what a reload
   * is, and what the incident in #215 actually was.
   */
  const [identity, setIdentity] = useState<TerminalIdentity | 'unknown'>('unknown');
  const [shift, setShift] = useState<ActiveShift | null | 'unknown'>('unknown');

  /** What the held copy holds, or `null` until it has been read. */
  const [offered, setOffered] = useState<Offered | null>(null);

  /**
   * Which counter is on screen, once a cashier has chosen one.
   *
   * `null` means they have not, and the counter below falls back to what the
   * catalogue offers. Held rather than derived so that a choice survives the
   * catalogue being replaced underneath it — a shop till whose copy is refreshed
   * mid-shift should not be thrown back to admission.
   */
  const [chosen, setChosen] = useState<Counter | null>(null);

  /**
   * What this device settled about its own counters, if it ever had to.
   *
   * `null` for almost every till on almost every day, and that is the ordinary
   * answer rather than a gap — most terminals have always had their own
   * counters. Read once, like the identity beside it: a recovery happens at
   * registration or at boot, both of which precede this screen, so there is
   * nothing here to poll for.
   */
  const [recovery, setRecovery] = useState<SequenceRecoveryRecord | null>(null);

  /**
   * Every registration this machine holds, one per outlet it sells for (#243),
   * and which one the counter is on. `[]` on a shell that cannot say, which
   * draws exactly the till a single registration always had.
   */
  const [held, setHeld] = useState<readonly HeldRegistration[] | 'unknown'>('unknown');

  /**
   * The last list the device answered with, for a re-read that fails (#254).
   * Since the outlet can change mid-shift, dropping the list there would take
   * the plate and the switch off a counter that is still selling — somewhere.
   */
  const lastHeld = useRef<readonly HeldRegistration[]>([]);

  /**
   * Bumped to read everything again after a switch. Every answer above belongs
   * to the registration the counter was on — its lease, its shift, its
   * catalogue, its counters — so after choosing another, none of them may be
   * shown until the device has answered for the new one.
   */
  const [reads, setReads] = useState(0);

  useEffect(() => {
    if (terminal === null || shiftBridge === null) {
      return;
    }

    let live = true;

    void terminal
      .identity()
      .then((answer) => {
        if (live) setIdentity(answer);
      })
      .catch((error: unknown) => {
        console.error('till: the device identity could not be read', error);

        // A device whose own main process will not answer for its identity is
        // not a device that can sell, and this is the standing it has always
        // been shown as when the call failed. It is not strictly what happened
        // — the honest answer is that the shell did not respond, which
        // `TILL_UNAVAILABLE` has words for — and that is noted in the #215 PR
        // as a separate thing to put right rather than changed here.
        if (live) setIdentity({ registered: false, status: 'unregistered' });
      });

    // Read rather than assumed, because a shift survives a reload of this window
    // — it lives in the main process — and a cashier who refreshed the screen
    // mid-queue must not be asked for their PIN again.
    void shiftBridge
      .current()
      .then((answer) => {
        if (live) setShift(answer);
      })
      .catch((error: unknown) => {
        console.error('till: the shift could not be read', error);
        if (live) setShift(null);
      });

    // Read from the held copy with no round trip, the way the selling screens
    // do. It decides which counter this terminal opens on and whether there is
    // anything to switch to; a device that has never fetched one offers neither,
    // and the admission screen says so.
    void catalogue
      ?.held()
      .then((held) => {
        if (!live) return;

        const items = held?.items ?? [];

        setOffered({
          admission: items.some((item) => item.kind === 'TICKET_TYPE' || item.kind === 'ACTIVITY'),
          goods: items.some((item) => item.kind === 'PRODUCT'),
        });
      })
      .catch((error: unknown) => {
        console.error('till: the catalogue could not be read', error);
        if (live) setOffered({ admission: false, goods: false });
      });

    // Why this terminal's numbering starts where it does, where that needs
    // saying at all. A shell without the channel answers nothing and the counter
    // draws nothing, which is the same as a till that never had to recover —
    // there is no failure here to report.
    void sequences
      ?.recovery()
      .then((settled) => {
        if (live) setRecovery(settled);
      })
      .catch((error: unknown) => {
        console.error('till: the counter recovery record could not be read', error);
      });

    // Which outlets this machine sells for. A failure here keeps the last list
    // read — empty on a first read, which costs the plate and the switch and not
    // the till — and which one the counter is on comes from the identity.
    if (registrationsBridge === null) {
      setHeld([]);
    } else {
      void registrationsBridge
        .registrations()
        .then((answer) => {
          lastHeld.current = answer;
          if (live) setHeld(answer);
        })
        .catch((error: unknown) => {
          console.error('till: the registrations could not be read', error);
          if (live) setHeld(lastHeld.current);
        });
    }

    return () => {
      live = false;
    };
  }, [terminal, shiftBridge, catalogue, sequences, registrationsBridge, reads]);

  // `sales` is deliberately **not** in this list. A shell one release behind this
  // bundle has no sales channel, and refusing the whole till over it would turn
  // off a gate that sells admission perfectly well — which is the version-skew
  // failure `desktop/bridge.ts` argues against in as many words. A missing sales
  // channel takes the shop counter away, not the till.
  if (catalogue === null || tickets === null || shiftBridge === null || terminal === null) {
    return (
      <TillFrame title="Till">
        <div className="my-auto w-full max-w-md self-center">
          <Alert>
            <IconDeviceDesktopOff />
            <AlertTitle>There is no till here</AlertTitle>
            <AlertDescription>{NOT_A_TILL}</AlertDescription>
          </Alert>
        </div>
      </TillFrame>
    );
  }

  if (identity === 'unknown' || shift === 'unknown' || offered === null || held === 'unknown') {
    // Deliberately blank rather than a spinner. Both answers are local IPC calls
    // that resolve in a frame or two, and a flash of loading chrome on the
    // screen a cashier opens every morning is worse than a beat of nothing.
    return <TillFrame title="Till" />;
  }

  // Which one the counter is on, by the identity just read and not by the
  // list's own flag: a list kept from before a switch still flags the outlet
  // left. One registration per outlet, so the outlet names it.
  const active = identity.registered
    ? held.find((registration) => registration.terminal.outletId === identity.outletId)
    : undefined;

  // Named where it sells whenever the device knows a name — and by prefix where
  // it holds more than one registration and does not, because telling them
  // apart is the whole point. A till with one unnamed registration draws what
  // it always drew.
  const outlet =
    active === undefined
      ? undefined
      : (active.outletName ?? (held.length > 1 ? active.terminal.prefix : undefined));

  // Somewhere else to sell: live, and not the registration the counter is on.
  const elsewhere = held.filter(
    (registration) => registration !== active && registration.terminal.status === 'live',
  );
  // Sell for rings up admission only (#284), so a goods-only shop is a switch
  // away, never a join (#313).
  const sellFor = offeredForSale(elsewhere);

  // Resolves with the words for a refusal, or `null` once the counter has moved.
  const switchTo = async (id: number): Promise<string | null> => {
    const chosenOutlet = elsewhere.find((registration) => registration.id === id);
    const name = chosenOutlet?.outletName ?? chosenOutlet?.terminal.prefix ?? 'that outlet';

    if (registrationsBridge === null) return messageForSwitchRefusal('REGISTRATION_NOT_HELD', name);

    try {
      const outcome = await registrationsBridge.activate(id);

      // Refused, and nothing moved: the counter is still selling where it was,
      // as whoever it was selling as. The outlet chosen does not have them.
      if (!outcome.activated) return messageForSwitchRefusal(outcome.reason, name);
    } catch (error) {
      // No answer, so the counter may have moved or may not. Read where it is
      // rather than guess: the plate has to name the outlet the next sale
      // lands at, and a message beside a plate that could be wrong is worse.
      console.error('till: could not switch outlets', error);
    }

    // Forget everything that belonged to the registration left behind and read
    // it all again — its lease, its catalogue, its counters, and the shift the
    // main process carried across or did not. A basket rung up there goes with
    // the screen it was on: it was priced from the other outlet's catalogue.
    // (A visitor buying at two outlets is one basket without switching — each
    // line priced by its own outlet, #284 — so this rule stands.)
    setIdentity('unknown');
    setShift('unknown');
    setOffered(null);
    setChosen(null);
    setRecovery(null);
    setHeld('unknown');
    setReads((count) => count + 1);

    return null;
  };

  const switcher = (layout?: 'row') =>
    elsewhere.length > 0 ? (
      <OutletSwitch outlets={elsewhere} onSwitch={switchTo} layout={layout} />
    ) : undefined;

  // **The gate is the status, not `registered`.** A withdrawn or expired device
  // is still registered — it knows its outlet and its prefix — and every route
  // that takes money refuses it, so a counter drawn for one is a counter that
  // can only disappoint the person at it. Narrowing here on the status is also
  // what leaves `identity` below as a device that may trade, without a second
  // check saying so.
  //
  // All three non-live standings land on one screen, because none of them can be
  // recovered from by anybody standing at this terminal: each needs an owner, or
  // the network, or both. What differs between them is the words, and those are
  // in `till-messages.ts`.
  if (identity.status !== 'live') {
    return (
      <TillFrame
        title="Till"
        outlet={outlet}
        standing={identity.registered ? identity.prefix : undefined}
        version={version}
      >
        <TerminalOutOfService
          status={identity.status}
          terminal={identity.registered ? identity.prefix : undefined}
          switcher={switcher()}
        />
      </TillFrame>
    );
  }

  if (shift === null) {
    return (
      <TillFrame title="Till" outlet={outlet} standing={identity.prefix}>
        <StartShift
          shift={shiftBridge}
          terminal={identity.prefix}
          switcher={switcher('row')}
          updates={desktopUpdates()}
          version={version}
          printer={
            printerSetup !== null && (
              <PrinterSetup printer={printerSetup} listsPrinters={listsPrinters} />
            )
          }
          onStarted={setShift}
        />
      </TillFrame>
    );
  }

  // What this outlet sells *and* what this shell can sell it through. The second
  // half only ever bites on a shell older than this bundle; on a current one it
  // is always true.
  const sellsGoods = offered.goods && sales !== null;

  // A shop till opens on the basket and a gate opens on admission. Where the
  // outlet sells both, or neither, admission leads — it is the older screen and
  // the one whose empty state explains how a till is given something to sell.
  const counter: Counter = chosen ?? (sellsGoods && !offered.admission ? 'goods' : 'admission');

  // Only where there is something to switch to. Nine tills in ten stand at an
  // outlet that does one of these, and a permanent pair of tabs on those would
  // be a control that never does anything and a second place to mis-tap.
  const bothCounters = offered.admission && sellsGoods;

  const sync = desktopSync();

  // Where this outlet asks for the visitor's window (#277), the clock that
  // prefills it — the device's own, as the paper prints every other time.
  const timeSlot = active?.timeSlot.ask === true ? { clock: systemClock } : undefined;

  const endShift = () => {
    setShift(null);
    // The next person at this till gets the counter their outlet leads with
    // rather than the one the last cashier happened to leave open.
    setChosen(null);
  };

  // Stated where both counters can see it, and above the choice between them:
  // it is about the terminal rather than about what is being sold at it.
  const numbering = sequenceRecoveryNotice(recovery, new Date(systemClock()));

  return (
    <TillFrame
      title="Till"
      outlet={outlet}
      shared={held.length > 1}
      // Mid-shift, and beside the plate: the cashier stays signed in (#254).
      switcher={
        outlet !== undefined && (
          <OutletSwitchDialog
            current={outlet}
            cashier={shift.displayName}
            outlets={elsewhere}
            onSwitch={switchTo}
          />
        )
      }
      standing={`${identity.prefix} · ${shift.staffCode}`}
      version={version}
    >
      {numbering && (
        <div className="mx-auto mb-6 w-full max-w-[84rem] rounded-lg border border-border bg-muted/40 px-5 py-4">
          <p className={tillPlate}>Numbering carried over</p>
          {/* Muted and not `destructive`. Red on this system means the server
              refused something or is about to, and nothing has been refused —
              this is provenance, and the till is selling normally. */}
          <p className="mt-2 text-sm text-muted-foreground">
            This terminal was set up again with no record of its own numbering, so it carried on
            from what the records already held rather than starting over.
          </p>
          <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
            {numbering.counters.map(({ kind, resumesAt }) => (
              <li key={kind} className="text-sm font-medium text-foreground">
                {counterName(kind)} resume at {resumesAt}
              </li>
            ))}
          </ul>
        </div>
      )}

      {bothCounters && (
        <div className="mx-auto mb-6 flex w-full max-w-[84rem] items-center gap-3">
          <span className={tillPlate}>Counter</span>

          {/* Composed from buttons for the reason `SellGoods` gives about its
              payment modes: the registry has no toggle group, and adding one
              means a `shadcn add` that has silently overwritten the button's
              touch sizes three times. */}
          <div role="radiogroup" aria-label="Counter" className="flex gap-2">
            {(
              [
                { value: 'admission', label: 'Admission' },
                { value: 'goods', label: 'Shop' },
              ] as const
            ).map(({ value, label }) => (
              <Button
                key={value}
                role="radio"
                aria-checked={counter === value}
                // Neutral either way. The one accent on a till screen belongs to
                // the action that takes money, not to the tab above it.
                variant={counter === value ? 'secondary' : 'outline'}
                size="touch"
                onClick={() => {
                  setChosen(value);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {counter === 'goods' && sales !== null ? (
        <SellGoods
          bridge={{
            catalogue,
            sales,
            takings,
            recent,
            shift: shiftBridge,
            sync,
            printer,
            stock,
          }}
          shift={shift}
          onShiftEnded={endShift}
          timeSlot={timeSlot}
        />
      ) : (
        <SellAdmission
          // `sales` too since #274: a taxed party is one sale on the shop
          // counter's path; `null` sells admission as tickets. `recent` since
          // #290: the shop's Today's sales.
          bridge={{
            catalogue,
            tickets,
            sales,
            recent,
            shift: shiftBridge,
            sync,
          }}
          shift={shift}
          onShiftEnded={endShift}
          timeSlot={timeSlot}
          // Only where there is somewhere else to sell, so a till at one outlet
          // draws exactly the screen it always did.
          across={
            salesAcross !== null && outlet !== undefined && sellFor.length > 0
              ? { here: outlet, outlets: sellFor, sales: salesAcross, clock: systemClock }
              : undefined
          }
        />
      )}
    </TillFrame>
  );
}
